import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import GavelIcon from '@mui/icons-material/Gavel'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import moment from 'moment'
import BigModal from '../../../../../components/Modals/BigModal'
import API from '../../../../../services/API'
import { Add, PlaylistAdd } from '@mui/icons-material'
import SelectedDecision from './SelectedDecision'

const DecisionCard = ({ decision, onView }) => {
  const chosenOption = decision.optionsConsidered?.find((x) => x.chosen)

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 4,
        border: '1px solid #E2E8F0',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 8px 24px rgba(36,47,64,0.06)',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Stack>
            <Typography fontWeight={900} color="#242F40">
              {decision.decisionNumber || 'Complex Decision'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Started{' '}
              {moment(
                decision.decisionMakingStarted || decision.createdAt,
              ).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Stack>

          <Chip
            label={decision.status}
            size="small"
            sx={{
              borderRadius: 999,
              fontWeight: 900,
              backgroundColor:
                decision.status === 'Completed' ? '#DCFCE7' : '#E6F7F9',
              color: decision.status === 'Completed' ? '#166534' : '#1E7F88',
            }}
          />
        </Stack>

        <Divider />

        <Stack spacing={0.5}>
          <Typography variant="caption" color="text.secondary" fontWeight={800}>
            PROBLEM
          </Typography>
          <Typography variant="body2">{decision.problem}</Typography>
        </Stack>

        {decision.finalDecision && (
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={800}
            >
              FINAL DECISION
            </Typography>
            <Typography fontWeight={800} color="#242F40">
              {decision.finalDecision}
            </Typography>
          </Stack>
        )}

        {chosenOption && (
          <Stack spacing={0.5}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={800}
            >
              CHOSEN OPTION
            </Typography>
            <Typography variant="body2">{chosenOption.option}</Typography>
          </Stack>
        )}

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Chip
            label={`${decision.briefFacts?.length || 0} facts`}
            size="small"
            sx={{ borderRadius: 999 }}
          />
          <Chip
            label={`${decision.optionsConsidered?.length || 0} options`}
            size="small"
            sx={{ borderRadius: 999 }}
          />
          <Chip
            label={`${decision.actions?.length || 0} actions`}
            size="small"
            sx={{ borderRadius: 999 }}
          />
        </Stack>

        <Button
          size="small"
          endIcon={<OpenInNewIcon />}
          onClick={onView}
          sx={{
            alignSelf: 'flex-start',
            borderRadius: 999,
            textTransform: 'none',
            fontWeight: 800,
            color: '#239AA4',
          }}
        >
          View full decision
        </Button>
      </Stack>
    </Paper>
  )
}

const DecisionRegister = ({
  open,
  onClose,
  controlRoomID,
  openComplexDecision,
  setOpenNewComplexDecision,
  setOpenComplexDecision,
  // Recording complex decisions is a commander action — everyone else can
  // still review the register.
  canCreate = true,
}) => {
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [decisions, setDecisions] = useState([])

  useEffect(() => {
    if (!open || !controlRoomID) return

    setLoading(true)
    setFetchError(false)

    API.get(`/commandcontrol/complex-decisions/${controlRoomID}`)
      .then((res) => {
        if (res.status === 200) {
          setDecisions(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [open, controlRoomID])

  return (
    <BigModal
      open={open}
      onClose={onClose}
      style={{ width: { xs: '94vw', md: '80%' }, maxWidth: { xs: '94vw', md: '90vw' } }}
    >
      <Box sx={{ width: '100%', minWidth: { md: 760 } }}>
        <Box
          sx={{
            mx: -3,
            mt: -3,
            mb: 3,
            p: 3,
            px: { xs: 3, md: 6 },
            color: 'white',
            background:
              'linear-gradient(135deg, #1A2230 0%, #242F40 55%, #239AA4 150%)',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: 3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.22)',
                }}
              >
                <GavelIcon sx={{ fontSize: 30 }} />
              </Box>

              <Stack>
                <Typography variant="overline" sx={{ opacity: 0.72 }}>
                  Command Decision Log
                </Typography>
                <Typography variant="h5" fontWeight={900} color="white">
                  Decision Register
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ opacity: 0.78 }}
                  color="white"
                >
                  Review complex decisions recorded for this control room.
                </Typography>
              </Stack>
            </Stack>
            {canCreate && (
              <IconButton
                onClick={() => setOpenNewComplexDecision(true)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.10)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.18)' },
                }}
              >
                <Add />
              </IconButton>
            )}
          </Stack>
        </Box>

        <Stack spacing={2}>
          {loading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress sx={{ color: '#239AA4' }} />
            </Stack>
          )}

          {fetchError && (
            <Alert severity="error" sx={{ borderRadius: 3 }}>
              Unable to load decisions.
            </Alert>
          )}

          {!loading && !fetchError && decisions.length === 0 && (
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              No complex decisions have been recorded yet.
            </Alert>
          )}

          {!loading &&
            decisions.map((decision) => (
              <DecisionCard
                key={decision._id}
                decision={decision}
                onView={() => setOpenComplexDecision(decision)}
              />
            ))}
        </Stack>
      </Box>
      <SelectedDecision
        decision={openComplexDecision}
        onClose={() => setOpenComplexDecision(null)}
      />
    </BigModal>
  )
}

export default DecisionRegister
